import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControlLabel,
  Switch,
  Box,
  Chip,
  Typography,
  IconButton,
  Checkbox,
  FormGroup,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import { Close } from "@mui/icons-material";
import LoadingButton from "../../../components/LoadingButton";
import MultiCollaboratorSelect from "../../../components/MultiCollaboratorSelect";
import SmartListPicker from "../../../components/SmartListPicker";
import { KeywordMonitor, CreateMonitorRequest, UpdateMonitorRequest, MonitorPlatform, MonitorFrequency } from "../types";
import {
  KEYWORD_MONITOR_PLATFORM_REDDIT,
  KEYWORD_MONITOR_PLATFORM_HACKERNEWS,
  KEYWORD_MONITOR_PLATFORM_LINKEDIN,
  KEYWORD_MONITOR_PLATFORM_X,
  KEYWORD_MONITOR_PLATFORM_QUORA,
  KEYWORD_MONITOR_FREQUENCY_HOURLY,
  KEYWORD_MONITOR_FREQUENCY_3H,
  KEYWORD_MONITOR_FREQUENCY_6H,
  KEYWORD_MONITOR_FREQUENCY_12H,
  KEYWORD_MONITOR_FREQUENCY_DAILY,
  KEYWORD_MONITOR_FREQUENCY_WEEKLY,
  KEYWORD_MONITOR_PLATFORM_CHATGPT,
  KEYWORD_MONITOR_PLATFORM_GOOGLE_AI,
} from "../../../constants";

interface MonitorFormDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (data: CreateMonitorRequest | UpdateMonitorRequest) => Promise<void>;
  monitor?: KeywordMonitor | null;
  companyId: string;
  getToken: () => Promise<string>;
}

const MonitorFormDialog: React.FC<MonitorFormDialogProps> = ({
  open,
  onClose,
  onSave,
  monitor,
  companyId,
  getToken,
}) => {
  const [name, setName] = useState("");
  const [keywordsInput, setKeywordsInput] = useState("");
  const [keywords, setKeywords] = useState<string[]>([]);
  const [platforms, setPlatforms] = useState<MonitorPlatform[]>([
    "reddit",
    "hackernews",
  ]);
  const [subredditsInput, setSubredditsInput] = useState("");
  const [subreddits, setSubreddits] = useState<string[]>([]);
  const [aiFilterEnabled, setAiFilterEnabled] = useState(true);
  const [notifyEmail, setNotifyEmail] = useState(true);
  const [notifyInApp, setNotifyInApp] = useState(true);
  const [notifyUserIds, setNotifyUserIds] = useState<string[]>([]);
  const [syncToListId, setSyncToListId] = useState<string>("");
  const [chatgptPrompt, setChatgptPrompt] = useState("");
  const [aiFilterInstructions, setAiFilterInstructions] = useState("");
  const [checkFrequency, setCheckFrequency] = useState<MonitorFrequency>(KEYWORD_MONITOR_FREQUENCY_HOURLY as MonitorFrequency);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isEdit = !!monitor;

  const platformCreditCosts: Record<string, number> = {
    [KEYWORD_MONITOR_PLATFORM_CHATGPT]: 6,
    [KEYWORD_MONITOR_PLATFORM_GOOGLE_AI]: 2,
  };
  const totalCreditsPerRun = platforms.reduce(
    (sum, p) => sum + (platformCreditCosts[p] || 1),
    0
  ) * keywords.length;

  useEffect(() => {
    if (monitor) {
      setName(monitor.name);
      setKeywords(monitor.keywords);
      setKeywordsInput("");
      setPlatforms(monitor.platforms);
      setSubreddits(monitor.filters?.subreddits || []);
      setSubredditsInput("");
      setChatgptPrompt(monitor.filters?.chatgptPrompt || "");
      setAiFilterInstructions(monitor.filters?.aiFilterInstructions || "");
      setAiFilterEnabled(monitor.aiFilterEnabled);
      setNotifyEmail(monitor.notifyEmail);
      setNotifyInApp(monitor.notifyInApp);
      setNotifyUserIds(monitor.notifyUserIds || []);
      setSyncToListId(monitor.syncToListId || "");
      setCheckFrequency(monitor.checkFrequency || KEYWORD_MONITOR_FREQUENCY_HOURLY as MonitorFrequency);
    } else {
      resetForm();
    }
  }, [monitor, open]);

  const resetForm = () => {
    setName("");
    setKeywordsInput("");
    setKeywords([]);
    setPlatforms(["reddit", "hackernews"]);
    setSubredditsInput("");
    setSubreddits([]);
    setChatgptPrompt("");
    setAiFilterInstructions("");
    setAiFilterEnabled(true);
    setNotifyEmail(true);
    setNotifyInApp(true);
    setNotifyUserIds([]);
    setSyncToListId("");
    setCheckFrequency(KEYWORD_MONITOR_FREQUENCY_HOURLY as MonitorFrequency);
    setError(null);
  };

  const handleAddKeyword = () => {
    const newKeywords = keywordsInput
      .split(",")
      .map((k) => k.trim())
      .filter((k) => k && !keywords.includes(k));
    if (newKeywords.length > 0) {
      setKeywords([...keywords, ...newKeywords]);
      setKeywordsInput("");
    }
  };

  const handleRemoveKeyword = (keyword: string) => {
    setKeywords(keywords.filter((k) => k !== keyword));
  };

  const handleAddSubreddit = () => {
    const newSubreddits = subredditsInput
      .split(",")
      .map((s) => s.trim().replace(/^r\//, ""))
      .filter((s) => s && !subreddits.includes(s));
    if (newSubreddits.length > 0) {
      setSubreddits([...subreddits, ...newSubreddits]);
      setSubredditsInput("");
    }
  };

  const handleRemoveSubreddit = (subreddit: string) => {
    setSubreddits(subreddits.filter((s) => s !== subreddit));
  };

  const handlePlatformChange = (platform: MonitorPlatform) => {
    if (platforms.includes(platform)) {
      if (platforms.length > 1) {
        setPlatforms(platforms.filter((p) => p !== platform));
      }
    } else {
      setPlatforms([...platforms, platform]);
    }
  };

  const handleSubmit = async () => {
    if (!name.trim()) {
      setError("Monitor name is required");
      return;
    }
    if (keywords.length === 0) {
      setError("At least one keyword is required");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const data: CreateMonitorRequest | UpdateMonitorRequest = {
        name: name.trim(),
        keywords,
        platforms,
        filters: {
          subreddits: subreddits.length > 0 ? subreddits : undefined,
          chatgptPrompt: chatgptPrompt.trim() || undefined,
          aiFilterInstructions: aiFilterInstructions.trim() || undefined,
        },
        aiFilterEnabled,
        notifyEmail,
        notifyInApp,
        notifyUserIds,
        syncToListId: syncToListId || null,
        checkFrequency,
      };

      await onSave(data);
      onClose();
    } catch (err: any) {
      setError(
        err.response?.data?.error || err.message || "Failed to save monitor"
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          {isEdit ? "Edit Monitor" : "Create Monitor"}
          <IconButton size="small" onClick={onClose}>
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <TextField
          fullWidth
          label="Monitor Name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          sx={{ mb: 2, mt: 1 }}
          placeholder="e.g., My Product Mentions"
        />

        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Keywords
          </Typography>
          <Box sx={{ display: "flex", gap: 1, mb: 1 }}>
            <TextField
              fullWidth
              size="small"
              value={keywordsInput}
              onChange={(e) => setKeywordsInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  handleAddKeyword();
                }
              }}
              placeholder="Enter keywords (comma separated)"
            />
            <Button variant="outlined" onClick={handleAddKeyword}>
              Add
            </Button>
          </Box>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
            {keywords.map((keyword) => (
              <Chip
                key={keyword}
                label={keyword}
                onDelete={() => handleRemoveKeyword(keyword)}
                size="small"
              />
            ))}
          </Box>
        </Box>

        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Platforms
          </Typography>
          <FormGroup row sx={{ flexWrap: "wrap" }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={platforms.includes(KEYWORD_MONITOR_PLATFORM_REDDIT)}
                  onChange={() => handlePlatformChange(KEYWORD_MONITOR_PLATFORM_REDDIT)}
                />
              }
              label="Reddit"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={platforms.includes(KEYWORD_MONITOR_PLATFORM_HACKERNEWS)}
                  onChange={() => handlePlatformChange(KEYWORD_MONITOR_PLATFORM_HACKERNEWS)}
                />
              }
              label="Hacker News"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={platforms.includes(KEYWORD_MONITOR_PLATFORM_LINKEDIN)}
                  onChange={() => handlePlatformChange(KEYWORD_MONITOR_PLATFORM_LINKEDIN)}
                />
              }
              label="LinkedIn"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={platforms.includes(KEYWORD_MONITOR_PLATFORM_X)}
                  onChange={() => handlePlatformChange(KEYWORD_MONITOR_PLATFORM_X)}
                />
              }
              label="X (Twitter)"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={platforms.includes(KEYWORD_MONITOR_PLATFORM_QUORA)}
                  onChange={() => handlePlatformChange(KEYWORD_MONITOR_PLATFORM_QUORA)}
                />
              }
              label="Quora"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={platforms.includes(KEYWORD_MONITOR_PLATFORM_CHATGPT)}
                  onChange={() => handlePlatformChange(KEYWORD_MONITOR_PLATFORM_CHATGPT)}
                />
              }
              label="ChatGPT (6 credits)"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={platforms.includes(KEYWORD_MONITOR_PLATFORM_GOOGLE_AI)}
                  onChange={() => handlePlatformChange(KEYWORD_MONITOR_PLATFORM_GOOGLE_AI)}
                />
              }
              label="Google AI (2 credits)"
            />
          </FormGroup>
        </Box>

        <Box sx={{ mb: 2 }}>
          <FormControl fullWidth size="small">
            <InputLabel>Check Frequency</InputLabel>
            <Select
              value={checkFrequency}
              label="Check Frequency"
              onChange={(e) => setCheckFrequency(e.target.value as MonitorFrequency)}
            >
              <MenuItem value={KEYWORD_MONITOR_FREQUENCY_HOURLY}>Hourly</MenuItem>
              <MenuItem value={KEYWORD_MONITOR_FREQUENCY_3H}>Every 3 hours</MenuItem>
              <MenuItem value={KEYWORD_MONITOR_FREQUENCY_6H}>Every 6 hours</MenuItem>
              <MenuItem value={KEYWORD_MONITOR_FREQUENCY_12H}>Every 12 hours</MenuItem>
              <MenuItem value={KEYWORD_MONITOR_FREQUENCY_DAILY}>Daily (8 AM PST)</MenuItem>
              <MenuItem value={KEYWORD_MONITOR_FREQUENCY_WEEKLY}>Weekly (Mondays 8 AM PST)</MenuItem>
            </Select>
          </FormControl>
          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: "block" }}>
            How often to check for new mentions
          </Typography>
        </Box>

        {platforms.includes(KEYWORD_MONITOR_PLATFORM_REDDIT) && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Subreddit Filter (optional)
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: "block" }}>
              Leave empty to search all of Reddit, or add specific subreddits
            </Typography>
            <Box sx={{ display: "flex", gap: 1, mb: 1 }}>
              <TextField
                fullWidth
                size="small"
                value={subredditsInput}
                onChange={(e) => setSubredditsInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    handleAddSubreddit();
                  }
                }}
                placeholder="e.g., startups, SaaS (comma separated)"
              />
              <Button variant="outlined" onClick={handleAddSubreddit}>
                Add
              </Button>
            </Box>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
              {subreddits.map((subreddit) => (
                <Chip
                  key={subreddit}
                  label={`r/${subreddit}`}
                  onDelete={() => handleRemoveSubreddit(subreddit)}
                  size="small"
                />
              ))}
            </Box>
          </Box>
        )}

        {(platforms.includes(KEYWORD_MONITOR_PLATFORM_CHATGPT) || platforms.includes(KEYWORD_MONITOR_PLATFORM_GOOGLE_AI)) && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              AI Prompt Template (optional)
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: "block" }}>
              Customize the prompt sent to AI platforms. Use {"{keyword}"} as a placeholder. Leave empty to use keywords directly.
            </Typography>
            <TextField
              fullWidth
              size="small"
              multiline
              rows={3}
              value={chatgptPrompt}
              onChange={(e) => setChatgptPrompt(e.target.value)}
              placeholder="e.g., What are the best {keyword} tools available today?"
            />
          </Box>
        )}

        <Box sx={{ mb: 2 }}>
          <FormControlLabel
            control={
              <Switch
                checked={aiFilterEnabled}
                onChange={(e) => setAiFilterEnabled(e.target.checked)}
              />
            }
            label="AI Filter"
          />
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", ml: 6 }}>
            Filter out irrelevant mentions using AI
          </Typography>
          {aiFilterEnabled && (
            <Box sx={{ mt: 1.5 }}>
              <TextField
                fullWidth
                size="small"
                multiline
                rows={3}
                label="AI Filter Instructions (optional)"
                value={aiFilterInstructions}
                onChange={(e) => setAiFilterInstructions(e.target.value)}
                placeholder="e.g., Skip job postings. Only show posts from people describing a problem they want solved. Ignore promotional content."
                helperText="Natural-language rules for the AI filter. Posts violating these will be scored low and dropped."
              />
            </Box>
          )}
        </Box>

        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Notifications
          </Typography>
          <FormGroup row>
            <FormControlLabel
              control={
                <Switch
                  checked={notifyEmail}
                  onChange={(e) => setNotifyEmail(e.target.checked)}
                />
              }
              label="Email"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={notifyInApp}
                  onChange={(e) => setNotifyInApp(e.target.checked)}
                />
              }
              label="In-App"
            />
          </FormGroup>
        </Box>

        {(notifyEmail || notifyInApp) && (
          <Box sx={{ mb: 2 }}>
            <MultiCollaboratorSelect
              companyId={companyId}
              getToken={getToken}
              value={notifyUserIds}
              onChange={(userIds) => setNotifyUserIds(userIds)}
              label="Notify Team Members"
              helperText="Select team members to receive notifications when new mentions are found"
              size="small"
            />
          </Box>
        )}

        <Box sx={{ mb: 2 }}>
          <SmartListPicker
            companyId={companyId}
            getToken={getToken}
            value={syncToListId}
            onChange={(listId) => setSyncToListId(listId)}
            label="Sync to Smart List (optional)"
            helperText="Automatically sync new mentions to a Smart List with all fields including suggested replies"
          />
        </Box>

        {keywords.length > 0 && platforms.length > 0 && (
          <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
            <Chip
              label={`${totalCreditsPerRun} credit${totalCreditsPerRun !== 1 ? "s" : ""} per run (${keywords.length} keyword${keywords.length !== 1 ? "s" : ""} × ${platforms.length} platform${platforms.length !== 1 ? "s" : ""})`}
              size="small"
              variant="outlined"
            />
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <LoadingButton
          variant="contained"
          onClick={handleSubmit}
          loading={loading}
        >
          {isEdit ? "Save Changes" : "Create Monitor"}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};

export default MonitorFormDialog;
